// @flow
export type SectionRowIndex = { row: number, section: number };
const DEFAULT_ACTIVE_INDEX: SectionRowIndex = { row: 0, section: 0 };
const INACTIVE_INDEX: SectionRowIndex = { row: -1, section: -1 };
const noop = () => undefined;

// $FlowFixMe Flow v0.104.0 upgrade supression.
type Item = *;
type Section = Array<Item>;

/**
 * This manager class keeps track of the active index within a SectionList.
 * The manager is quite tied to the usage of SectionList, but the logic is separated
 * such that other components can add callback functions and so the indexing logic
 * can be tested separately from the SectionList (viewer) component.
 */
export default class IndexManager {
  // The active index (section & row indices)
  index: SectionRowIndex;
  // The list of Sections for which we're keeping track of the index
  sections: Array<Section>;
  // called when updating this.index so components using this can update state
  // scrollIntoView should be set to true if the update was from a selectNext/Prev call
  // because those calls are typically made by the user with arrow keys, the select object
  // might be out ov iew
  updateCallback: (scrollIntoView?: boolean) => void;
  // so components can trigger selection side effects
  selectItem: (item: Item) => void;

  constructor(sections: Array<Section>) {
    this.index = Object.assign({}, DEFAULT_ACTIVE_INDEX);
    this.sections = sections;
    this.updateCallback = noop;
    this.selectItem = noop;
  }

  // return the currently selected section/row index object
  getIndex() {
    return this.index;
  }

  // returns true if the current selection is the first in the list (both section and row)
  isAtStart() {
    const { row: rowIndex, section: sectionIndex } = this.index;
    return rowIndex === 0 && sectionIndex === 0;
  }

  // returns true if the current selection is the last in the list (both section and row)
  isAtEnd() {
    const { row: rowIndex, section: sectionIndex } = this.index;
    const currSection = this.sections[sectionIndex] || [];
    return this.sections.length === sectionIndex + 1 && currSection.length === rowIndex + 1;
  }

  // sets the next index list as "active"; does nothing if we're already at end
  next() {
    if (this.isAtEnd()) {
      return;
    }

    const { row: rowIndex, section: sectionIndex } = this.index;
    if (rowIndex === INACTIVE_INDEX.row && sectionIndex === INACTIVE_INDEX.section) {
      this.index = Object.assign({}, DEFAULT_ACTIVE_INDEX);
    } else if (this.sections[sectionIndex].length > rowIndex + 1) {
      this.index.row += 1;
    } else {
      this.index.section += 1;
      this.index.row = 0;
    }
    this.updateCallback(true);
  }

  // sets the previous index list as "active"; does nothing if we're already at end
  prev() {
    if (this.isAtStart()) {
      return;
    }

    const { row: rowIndex, section: sectionIndex } = this.index;
    if (rowIndex === INACTIVE_INDEX.row && sectionIndex === INACTIVE_INDEX.section) {
      this.index = Object.assign({}, DEFAULT_ACTIVE_INDEX);
    } else if (rowIndex > 0) {
      this.index.row -= 1;
    } else {
      const newSection = this.index.section - 1;
      this.index = {
        section: newSection,
        row: this.sections[newSection].length - 1,
      };
    }
    this.updateCallback(true);
  }

  // calls the selection callback (the current active index is considered "selected")
  select() {
    const { row: rowIndex, section: sectionIndex } = this.index;
    this.selectItem((this.sections[sectionIndex] || [])[rowIndex]);
  }

  // remove the active index
  setInactive() {
    this.index = Object.assign({}, INACTIVE_INDEX);
    this.updateCallback();
  }

  // sets the selectItem function call
  setSelectItem(selectItem: (item: Item) => void) {
    this.selectItem = selectItem;
  }

  // sets the updateCallback function call
  setUpdateCallback(updateCallback: () => void) {
    this.updateCallback = updateCallback;
  }

  // directly updates the index to a specific section/row
  updateIndex(newIndex: SectionRowIndex) {
    this.index = newIndex;
    this.updateCallback();
  }
}
